/**
 * AI Integration Types
 */

export type AIProviderType = 'openai' | 'anthropic' | 'straico' | 'meta_llama';

export interface AIProvider {
  id: string;
  provider_type: AIProviderType;
  provider_display: string;
  name: string;
  has_api_key: boolean;
  api_endpoint?: string;
  default_endpoint: string;
  model_name?: string;
  temperature: number;
  max_tokens: number;
  is_active: boolean;
  status: 'active' | 'inactive' | 'error';
  status_message?: string;
  rate_limit_requests: number;
  rate_limit_window: number;
  total_requests: number;
  total_tokens: number;
  total_cost: number;
  last_used: string | null;
  created_at: string;
  updated_at: string;
  assigned_features?: AIFeatureArea[];
  assigned_feature_labels?: string[];
  is_default?: boolean;
  is_platform_managed?: boolean;
  context_injection?: string;
  data_query_context?: any;
}

export interface AIProviderCreate {
  provider_type: AIProviderType;
  name: string;
  api_key: string;
  api_endpoint?: string;
  model_name?: string;
  temperature?: number;
  max_tokens?: number;
}

export interface AIUsageStats {
  provider_type: string;
  provider_name: string;
  total_requests: number;
  total_tokens: number;
  total_cost: number;
  avg_response_time: number;
  success_rate: number;
  last_used: string;
}

export interface ChurchAIContext {
  id: string;
  church: string;
  church_name: string;
  mission_statement?: string;
  vision_statement?: string;
  core_values: string[];
  ministry_priorities: {
    priorities?: string[];
    [key: string]: any;
  };
  target_demographics: Record<string, any>;
  current_challenges?: string;
  growth_goals?: string;
  resource_constraints?: string;
  theological_emphasis?: string;
  worship_style?: string;
  community_description?: string;
  cultural_considerations?: string;
  custom_context: Record<string, any>;
  ai_personality: 'professional' | 'friendly' | 'pastoral' | 'academic';
  preferred_communication_style?: string;
  context_prompt: string;
  created_at: string;
  updated_at: string;
}

export interface AIRequestLog {
  id: string;
  provider: string;
  provider_name: string;
  request_type: string;
  prompt: string;
  prompt_tokens: number;
  response: string;
  response_tokens: number;
  total_tokens: number;
  user: string;
  user_email: string;
  feature: string;
  success: boolean;
  error_message?: string;
  response_time_ms: number;
  estimated_cost: number;
  created_at: string;
}

export interface AICompletionRequest {
  prompt: string;
  provider_id?: string;
  system_prompt?: string;
  temperature?: number;
  max_tokens?: number;
}

export interface AICompletionResponse {
  success: boolean;
  response?: string;
  provider?: {
    id: string;
    type: string;
    name: string;
  };
  usage?: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
  response_time_ms?: number;
  error?: string;
  message?: string;
}

// Add to existing types

export enum AIFeatureArea {
  ANALYTICS = 'analytics',
  ANALYTICS_CHAT = 'analytics_chat',
  KNOWLEDGE_BASE = 'knowledge_base',
  RECOMMENDATIONS = 'recommendations',
  ENGAGEMENT = 'engagement',
  STRATEGIC_PLANNING = 'strategic_planning',
  WEB_INTERFACE = 'web_interface',
  DEVOTIONAL_ENGINE = 'devotional_engine',
  LANGUAGE_ADAPTATION = 'language_adaptation'
}

export interface AIFeatureInfo {
  value: AIFeatureArea;
  label: string;
  platform_controlled: boolean;
}

export interface ProviderAssignment {
  label: string;
  provider_id: number;
  provider_name: string;
  provider_type: string | null;
}

export interface FeatureAssignments {
  [key: string]: ProviderAssignment;
  _default?: {
    provider_id: number;
    provider_name: string;
  };
}

export interface AssignmentUpdate {
  assignments: {
    [providerId: string]: AIFeatureArea[];
  };
  default?: string;
}

export interface ProviderCapabilities {
  id: number;
  name: string;
  type: string;
  assigned_features: AIFeatureArea[];
  feature_labels: string[];
  is_default: boolean;
  can_handle: number;
  is_active: boolean;
}

export interface FeatureCoverage {
  complete: boolean;
  missing_features: AIFeatureArea[];
  has_default_provider: boolean;
  coverage_percentage: number;
  uses_connectpath_managed: boolean;
}